import { computed, inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import {
  AsyncState,
  asResult,
  computedFailure,
  dataFromState,
  initialState,
  loadingState,
  successState,
} from '@libs/core/lib/state/async-states';
import {
  deleteProductReview,
  patchProductReview,
  productReviewCreate,
  productReviewsList,
} from '@libs/mtjr/shared/generated/entities/product-review/service/product-review.service';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withProductReviewDelete() {
  return signalStoreFeature(
    withState<{ productReviewDeleteState: AsyncState<void>; deleteId: string }>({
      productReviewDeleteState: initialState(),
      deleteId: '',
    }),
    withMethods((store, apiService = inject(ApiService)) => ({
      async productReviewDelete(id: string) {
        patchState(store, { productReviewDeleteState: loadingState(), deleteId: id });
        const result = await asResult(deleteProductReview(apiService, id));
        patchState(store, { productReviewDeleteState: result });
      },
    })),
    withComputed((store) => ({
      productReviewDeleting: computed(() => {
        if (store.productReviewDeleteState().type === 'loading' && store.deleteId() !== '') {
          return store.deleteId();
        }
        return null;
      }),
      productReviewDeleteFailure: computedFailure(store.productReviewDeleteState),
    })),
  );
}

export function withProductReviewPatch() {
  return signalStoreFeature(
    withState<{ productReviewPatchState: AsyncState<Dtos.UpdateResult> }>({
      productReviewPatchState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productReviewPatch(id: string, productReview: Partial<Entity.ProductReviewUpdate>) {
        patchState(store, { productReviewPatchState: loadingState() });
        const result = await asResult(patchProductReview(apiService, id, productReview));
        patchState(store, { productReviewPatchState: result });
      },
    })),
    withComputed((store) => ({
      productReviewPatchLoading: computed(() => store.productReviewPatchState().type === 'loading'),
      productReviewPatchFailure: computedFailure(store.productReviewPatchState),
    })),
  );
}

export function withProductReviewsList() {
  type ProductReviewsListState = {
    productReviewsListState: AsyncState<Dtos.ProductReviewsListResponse>;
    productReviewsListInput: Dtos.ProductReviewsListQueryParams;
    productReviewsListCanFetchNext: boolean;
    productReviewsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<ProductReviewsListState>({
      productReviewsListState: initialState(),
      productReviewsListInput: { page: 1, totalPerPage: 20 },
      productReviewsListCanFetchNext: false,
      productReviewsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productReviewsListPage(
        { productId }: Dtos.ProductReviewsListPathParams,
        input?: Partial<Dtos.ProductReviewsListQueryParams>,
      ) {
        const mergedInput = { ...store.productReviewsListInput(), ...input };
        patchState(store, {
          productReviewsListState: mergedInput.page === 1 ? loadingState() : store.productReviewsListState(),
          productReviewsListInput: mergedInput,
          productReviewsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(productReviewsList(apiService, store.productReviewsListInput(), { productId }));
        if (result.success) {
          const cState = store.productReviewsListState();
          let productReviews = result.data.productReviews;
          if (cState.success && mergedInput.page > 1) {
            productReviews = [...cState.data.productReviews, ...productReviews];
          }
          patchState(store, {
            productReviewsListState: successState({ ...result.data, productReviews: productReviews }),
            productReviewsListCanFetchNext: result.data.productReviews.length === mergedInput.totalPerPage,
            productReviewsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            productReviewsListState: result,
            productReviewsListCanFetchNext: false,
            productReviewsListFetchingNext: false,
          });
        }
      },

      async productReviewsListFetchNextPage({ productId }: Dtos.ProductReviewsListPathParams) {
        if (store.productReviewsListCanFetchNext() === false) return;
        const input = store.productReviewsListInput();
        await this.productReviewsListPage({ productId }, { ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      productReviews: dataFromState(store.productReviewsListState, (data) => data?.productReviews ?? []),
      productReviewsFailure: computedFailure(store.productReviewsListState),
    })),
  );
}

export function withProductReviewCreate() {
  return signalStoreFeature(
    withState<{ productReviewCreateState: AsyncState<Entity.ProductReview> }>({
      productReviewCreateState: initialState(),
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async productReviewCreate(productReviewCreateBody: Dtos.ProductReviewCreateBody) {
        patchState(store, { productReviewCreateState: loadingState() });
        const result = await asResult(productReviewCreate(apiService, productReviewCreateBody));
        patchState(store, { productReviewCreateState: result });
      },
    })),
    withComputed((store) => ({
      productReviewCreateLoading: computed(() => store.productReviewCreateState().type === 'loading'),
      productReviewCreateFailure: computedFailure(store.productReviewCreateState),
    })),
  );
}
